import { ApolloProvider } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { createManagementApolloClient } from 'lib-knowledge-management-common/apolloClient';
import { LanguageContextProvider } from 'lib-knowledge-management-common/contexts/languageProvider';
import { ModalProvider } from 'lib-knowledge-modal';
import { KnowledgeErrorBoundary } from 'lib-knowledge-utils/components/bugTracking';
import { KnowledgeErrorPlaceholder } from 'lib-knowledge-utils/components/knowledgeErrorPlaceholder';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import {
  SourceConversationsRenderer,
  SourceConversationsRendererProvider,
} from 'lib-knowledge-utils/components/sourceConversations/index';
import {
  NotificationContext,
  NotificationContextValue,
} from 'lib-knowledge-utils/contexts/notification';
import { RestConfigProvider } from 'lib-knowledge-utils/contexts/restConfig';
import {
  collectionRootPath,
  knowledgeSettingsPath,
} from 'lib-knowledge-utils/routes';
import { featureFlagsAtom } from 'lib-knowledge-utils/stores/featureFlags';
import { Suspense, useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useRoutes,
} from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import {
  AiDraftEventsProvider,
  SubscribeToAiDraftEvents,
} from './articleSuggestions/aiDraftEvents';
import { ArticleSuggestionsPage } from './articleSuggestions/articleSuggestions';
import { ArticlesPage } from './collection/pages/articles';
import CollectionEditPage from './collection/pages/collectionEdit';
import CollectionRootPage from './collection/pages/home';
import {
  NavigatePromptProvider,
  UseNavigatePrompt,
} from './hooks/useNavigatePrompt';
import { useShowArticleSuggestions } from './hooks/useShowArticleSuggestions';
import { getArticleEditorRoutes } from './routes';
import {
  AiSettingsEventsProvider,
  SubscribeToAiSettingsEvents,
} from './settings/aiSuggestions/aiSettingsEvents';
import { AiSuggestionsSettingsPage } from './settings/aiSuggestions/aiSuggestionsSettings';
import { AiSuggestionsVersionsPage } from './settings/aiSuggestions/aiSuggestionsVersions';
import KnowledgeSettingsPage, { GeneralSettings } from './settings/pages';

function CollectionRoutes() {
  const routes = (
    <Routes>
      <Route path="collection" element={<CollectionRootPage />} />
      <Route path="collection/:collectionId" element={<CollectionEditPage />} />
      <Route path="articles" element={<ArticlesPage />} />
    </Routes>
  );
  return routes;
}

function ArticleEditorRoutes() {
  const routes = getArticleEditorRoutes();
  return useRoutes(routes);
}

function ArticleSuggestionsRoutes() {
  // Gated here as well as in the nav. Redirecting rather than rendering nothing:
  // no other route matches this path, so a stale link would otherwise land on an
  // empty pane.
  const showArticleSuggestions = useShowArticleSuggestions();

  return (
    <Routes>
      <Route
        path="article-suggestions"
        element={
          showArticleSuggestions ? (
            <ArticleSuggestionsPage />
          ) : (
            <Navigate to={collectionRootPath.pattern} replace />
          )
        }
      />
    </Routes>
  );
}

/** Gated like the suggestions queue it tunes; a stale link lands on General. */
function AiSuggestionsSettingsGate({ children }: { children: JSX.Element }) {
  const showArticleSuggestions = useShowArticleSuggestions();
  return showArticleSuggestions ? (
    children
  ) : (
    <Navigate to={knowledgeSettingsPath.pattern} replace />
  );
}

function SettingsRoutes() {
  const routes = (
    <Routes>
      <Route path="settings" element={<KnowledgeSettingsPage />}>
        <Route index element={<GeneralSettings />} />
        <Route
          path="ai-suggestions"
          element={
            <AiSuggestionsSettingsGate>
              <AiSuggestionsSettingsPage />
            </AiSuggestionsSettingsGate>
          }
        />
        <Route
          path="ai-suggestions/versions"
          element={
            <AiSuggestionsSettingsGate>
              <AiSuggestionsVersionsPage />
            </AiSuggestionsSettingsGate>
          }
        />
        <Route
          path="ai-suggestions/versions/:versionId"
          element={
            <AiSuggestionsSettingsGate>
              <AiSuggestionsSettingsPage />
            </AiSuggestionsSettingsGate>
          }
        />
      </Route>
    </Routes>
  );
  return routes;
}

type KnowledgeFeatureFlags = {
  aiAutoTranslation: boolean;
  smartArticleCreateMultiConversation: boolean;
};

const ManagementEntry = ({
  notify,
  featureFlags,
  renderSourceConversations,
  subscribeToAiDraftEvents,
  subscribeToAiSettingsEvents,
  useNavigatePrompt,
}: {
  notify: NotificationContextValue;
  featureFlags: KnowledgeFeatureFlags;
  /**
   * Renders the conversations an AI-assisted article was drafted from, shown by
   * the chip in the article editor header. Supplied by the host app, which alone
   * can resolve conversation display data.
   */
  renderSourceConversations?: SourceConversationsRenderer;
  /**
   * Delivers the web socket's AI draft events, so the suggestions queue follows
   * drafts changed by the AI or another agent. Supplied by the host app, which
   * alone holds the socket. Without it the queue is a snapshot.
   */
  subscribeToAiDraftEvents?: SubscribeToAiDraftEvents;
  /**
   * Delivers the web socket's AI settings events, so the settings follow
   * changes made by another agent. Supplied by the host app, which alone holds
   * the socket. Without it the settings are a snapshot.
   */
  subscribeToAiSettingsEvents?: SubscribeToAiSettingsEvents;
  /**
   * Asks before navigating away from unsaved changes. Supplied by the host app,
   * which owns the router's blocking and the confirm dialog. Without it only
   * closing or reloading the tab asks.
   */
  useNavigatePrompt?: UseNavigatePrompt;
}): JSX.Element => {
  const routingLocation = useLocation();
  const setFeatureFlags = useSetAtom(featureFlagsAtom);

  useEffect(() => {
    setFeatureFlags({ featureFlags });
  }, [setFeatureFlags, featureFlags]);

  const managementClient = createManagementApolloClient();

  if (
    routingLocation.pathname === '/knowledge' ||
    routingLocation.pathname === '/knowledge/'
  ) {
    return <Navigate to={collectionRootPath.pattern} replace={true} />;
  }

  return (
    <KnowledgeErrorBoundary fallback={<KnowledgeErrorPlaceholder />}>
      <RestConfigProvider>
        <NotificationContext.Provider value={notify}>
          <ApolloProvider client={managementClient}>
            <Suspense fallback={<KnowledgeLoading />}>
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <LanguageContextProvider>
                  <SourceConversationsRendererProvider
                    value={renderSourceConversations}
                  >
                    <AiDraftEventsProvider value={subscribeToAiDraftEvents}>
                      <AiSettingsEventsProvider
                        value={subscribeToAiSettingsEvents}
                      >
                        <NavigatePromptProvider value={useNavigatePrompt}>
                          <ModalProvider>
                            <ArticleEditorRoutes />
                            <CollectionRoutes />
                            <ArticleSuggestionsRoutes />
                            <SettingsRoutes />
                          </ModalProvider>
                        </NavigatePromptProvider>
                      </AiSettingsEventsProvider>
                    </AiDraftEventsProvider>
                  </SourceConversationsRendererProvider>
                </LanguageContextProvider>
              </QueryParamProvider>
            </Suspense>
          </ApolloProvider>
        </NotificationContext.Provider>
      </RestConfigProvider>
    </KnowledgeErrorBoundary>
  );
};

export type {
  AiDraftEventHandlers,
  SubscribeToAiDraftEvents,
} from './articleSuggestions/aiDraftEvents';
export type { UseNavigatePrompt } from './hooks/useNavigatePrompt';
export type {
  AiSettingsEventHandlers,
  SubscribeToAiSettingsEvents,
} from './settings/aiSuggestions/aiSettingsEvents';

export default ManagementEntry;
